import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Alert,
  Tooltip,
  LinearProgress,
  Typography,
  Autocomplete,
  TextField,
  Checkbox,
  Chip,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DownloadIcon from "@mui/icons-material/Download";
import Paper, { PaperProps } from "@mui/material/Paper";
import Draggable from "react-draggable";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SyncIcon from "@mui/icons-material/Sync";
import { COLORS } from "../assets/theme";
import CustomButton from "./common/CustomButton";
import CustomLabel from "./common/CustomLabel";
import CustomTable from "./common/table/CustomTable";
import { IField, Params } from "../types/table/interfaces.type";
import {
  CdrExport,
  useCdrExportsQuery,
  deleteCdrExport,
  getExportFile,
  initiateMessageTrackerExport,
} from "../services/ExportStatusService";
import { CdrExportField } from "../services/MessageTrackerService";
import Actions from "./common/Actions";
import saveAs from "file-saver";
import ToastNotification from "./ToastNotification";
import { ReactComponent as Export } from "../assets/icons/export.svg";
import { hasJwtPermission } from "../services/ServicesHelpers";

const ALL_EXPORT_FIELDS = Object.values(CdrExportField);

const CLIENT_PRESET_FIELDS: CdrExportField[] = [
  CdrExportField.MESSAGE_ID,
  CdrExportField.CLIENT,
  CdrExportField.STATUS,
  CdrExportField.DELIVERY_STAT,
  CdrExportField.RATE,
  CdrExportField.POST_TRANSLATED_SOURCE,
  CdrExportField.POST_TRANSLATED_DESTINATION,
  CdrExportField.MESSAGE_TEXT,
  CdrExportField.COUNTRY,
  CdrExportField.OPERATOR,
  CdrExportField.MCC,
  CdrExportField.MNC,
  CdrExportField.DATE_RECEIVED,
];

const PROVIDER_PRESET_FIELDS: CdrExportField[] = [
  CdrExportField.PROVIDER,
  CdrExportField.STATUS,
  CdrExportField.DELIVERY_STAT,
  CdrExportField.COST,
  CdrExportField.POST_TRANSLATED_SOURCE,
  CdrExportField.POST_TRANSLATED_DESTINATION,
  CdrExportField.MESSAGE_TEXT,
  CdrExportField.COUNTRY,
  CdrExportField.OPERATOR,
  CdrExportField.MCC,
  CdrExportField.MNC,
  CdrExportField.PROVIDER_MESSAGE_ID,
  CdrExportField.DATE_SENT,
];

function formatFieldLabel(field: CdrExportField): string {
  return field
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function DraggablePaper(props: PaperProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      handle="#cdr-export-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

interface CdrExportDialogProps {
  open: boolean;
  onClose: () => void;
  exportParams: Params<any>;
  cdrCount?: number;
}

const CdrExportDialog: React.FC<CdrExportDialogProps> = ({
  open,
  onClose,
  exportParams,
  cdrCount,
}) => {
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [tableParams] = useState<Params<CdrExport>>({});
  const [downloadProgress, setDownloadProgress] = useState<
    Record<number, number>
  >({});
  const [selectedFields, setSelectedFields] = useState<CdrExportField[]>([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const allChecked = ALL_EXPORT_FIELDS.every((f) => selectedFields.includes(f));
  // With every field selected, only "All fields" shows as checked.
  const clientChecked =
    !allChecked && CLIENT_PRESET_FIELDS.every((f) => selectedFields.includes(f));
  const providerChecked =
    !allChecked && PROVIDER_PRESET_FIELDS.every((f) => selectedFields.includes(f));

  const handleAllPreset = () => {
    setSelectedFields(allChecked ? [] : ALL_EXPORT_FIELDS);
  };

  const handleClientPreset = () => {
    if (allChecked) {
      setSelectedFields(CLIENT_PRESET_FIELDS);
    } else if (clientChecked) {
      setSelectedFields((prev) => prev.filter((f) => !CLIENT_PRESET_FIELDS.includes(f)));
    } else {
      setSelectedFields((prev) => [
        ...new Set([
          ...prev.filter((f) => !PROVIDER_PRESET_FIELDS.includes(f)),
          ...CLIENT_PRESET_FIELDS,
        ]),
      ]);
    }
  };

  const handleProviderPreset = () => {
    if (allChecked) {
      setSelectedFields(PROVIDER_PRESET_FIELDS);
    } else if (providerChecked) {
      setSelectedFields((prev) => prev.filter((f) => !PROVIDER_PRESET_FIELDS.includes(f)));
    } else {
      setSelectedFields((prev) => [
        ...new Set([
          ...prev.filter((f) => !CLIENT_PRESET_FIELDS.includes(f)),
          ...PROVIDER_PRESET_FIELDS,
        ]),
      ]);
    }
  };

  const handleStartExport = async () => {
    try {
      setIsStarting(true);
      setStartError(null);
      const { countryId, ...restParams } = exportParams as any;
      const mappedParams = {
        ...restParams,
        ...(countryId !== undefined && { destinationCountryId: countryId }),
        fields: selectedFields,
      };
      await initiateMessageTrackerExport(mappedParams);
      ToastNotification.success("Export started successfully.");
    } catch (error: any) {
      setStartError(error.message || "Failed to start export.");
    } finally {
      setIsStarting(false);
    }
  };

  const handleDownload = async (id: string | number) => {
    const numId = Number(id);
    setDownloadProgress((prev) => ({ ...prev, [numId]: 0 }));
    try {
      const { filename, blob } = await getExportFile(String(id), (progress) => {
        setDownloadProgress((prev) => ({ ...prev, [numId]: progress }));
      });
      saveAs(blob, filename);
    } catch (error: any) {
      ToastNotification.error(error.message || "Failed to download export.");
    } finally {
      setDownloadProgress((prev) => {
        const next = { ...prev };
        delete next[numId];
        return next;
      });
    }
  };

  const handleDelete = async (id: string | number) => {
    try {
      await deleteCdrExport(Number(id));
    } catch (error: any) {
      ToastNotification.error(error.message || "Failed to delete export.");
    }
  };

  const CDR_EXPORT_LIMIT = 1_000_000;
  const exceedsLimit = cdrCount !== undefined && cdrCount > CDR_EXPORT_LIMIT;

  const exportButtonLabel = (() => {
    if (isStarting) return "Starting...";
    if (cdrCount !== undefined)
      return `Export (${cdrCount.toLocaleString()} CDRs)`;
    return "Export";
  })();

  const fields: IField<CdrExport>[] = [
    { key: "fileName", header: "File Name" },
    {
      key: "fileSize",
      header: "File Size",
      transform: (row: CdrExport) => {
        const bytes = row.fileSize;
        if (bytes == null) return "-";
        if (bytes >= 1024 * 1024)
          return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
        if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${bytes} B`;
      },
    },
    { key: "createdDate", header: "Created At" },
    { key: "createdBy", header: "User" },
    {
      key: "status",
      header: "Status",
      transform: (row: CdrExport) => {
        switch (row.status) {
          case "DONE":
            return (
              <Tooltip title="Done">
                <CheckCircleIcon
                  sx={{ color: "green", verticalAlign: "middle" }}
                />
              </Tooltip>
            );
          case "ERROR":
            return (
              <Tooltip title="Error">
                <CancelIcon sx={{ color: "red", verticalAlign: "middle" }} />
              </Tooltip>
            );
          case "RUNNING":
            return (
              <Tooltip title="Running">
                <SyncIcon
                  sx={{ color: COLORS.GOLD, verticalAlign: "middle" }}
                />
              </Tooltip>
            );
          case "QUEUED":
            return (
              <Tooltip title="Queued">
                <HourglassEmptyIcon
                  sx={{ color: "orange", verticalAlign: "middle" }}
                />
              </Tooltip>
            );
          default:
            return row.status;
        }
      },
    },
    { key: "count", header: "Count" },
    { key: "errorMessage", header: "Error Message" },
  ];

  const actions = Actions({
    onDownload: {
      handler: handleDownload,
      isDisabled: (row: CdrExport) =>
        row.status !== "DONE" || downloadProgress[row.id] !== undefined,
      icon: (row: CdrExport) => {
        const progress = downloadProgress[row.id];
        if (progress !== undefined) {
          return (
            <Box
              sx={{
                width: 64,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "2px",
              }}
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ width: "100%", height: 5, borderRadius: 3 }}
              />
              <Typography
                variant="caption"
                sx={{ fontSize: "0.6rem", lineHeight: 1 }}
              >
                {progress}%
              </Typography>
            </Box>
          );
        }
        const isDisabled =
          row.status !== "DONE" ||
          !hasJwtPermission("DOWNLOAD_CDR_EXPORT_FILE");
        return (
          <Tooltip title="Download">
            <DownloadIcon
              sx={{ color: isDisabled ? "#D3D3D3" : COLORS.GOLD, width: 23 }}
            />
          </Tooltip>
        );
      },
    },
    onDelete: {
      handler: handleDelete,
      isDisabled: !hasJwtPermission("DELETE_CDR_EXPORT"),
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      fullScreen={isSmallScreen}
      PaperComponent={isSmallScreen ? undefined : DraggablePaper}
      aria-labelledby="cdr-export-dialog-title"
      PaperProps={{
        sx: isSmallScreen
          ? {
              width: "100vw",
              maxWidth: "100vw",
              margin: 0,
              borderRadius: 0,
            }
          : {
              maxWidth: "1200px",
              borderRadius: "16px",
              minHeight: "480px",
            },
      }}
    >
      <DialogTitle
        id="cdr-export-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `2px solid ${COLORS.typographyColor}48`,
          cursor: isSmallScreen ? "default" : "move",
          py: 2,
          px: 3,
        }}
      >
        <CustomLabel
          text="CDR Exports"
          fontWeight={600}
          fontSize="1.3rem"
          sx={{ margin: 0 }}
        />
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3, pb: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {exceedsLimit && (
            <Alert severity="warning" sx={{ borderRadius: "8px", mt: 2 }}>
              Too many records to export ({cdrCount?.toLocaleString()} CDRs).
              Please adjust your filters to bring the count below{" "}
              {CDR_EXPORT_LIMIT.toLocaleString()}.
            </Alert>
          )}
          {startError && (
            <Alert severity="error" sx={{ borderRadius: "8px", mt: 2 }}>
              {startError}
            </Alert>
          )}

          <Box sx={{ mt: 3 }}>
            <Box sx={{ mb: 1 }}>
              <CustomLabel
                text="Export Fields"
                fontWeight={600}
                fontSize="0.85rem"
                sx={{ margin: 0 }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: "10px",
                bgcolor: `${COLORS.typographyColor}08`,
                border: `1px solid ${COLORS.typographyColor}18`,
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, mr: 1 }}>
                Presets:
              </Typography>
              <FormControlLabel
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600, userSelect: "none" }}>
                    Client
                  </Typography>
                }
                control={
                  <Checkbox
                    size="small"
                    checked={clientChecked}
                    onChange={handleClientPreset}
                    sx={{
                      p: 0.5,
                      color: COLORS.GOLD,
                      "&.Mui-checked": { color: COLORS.GOLD },
                    }}
                  />
                }
                sx={{ m: 0 }}
              />
              <FormControlLabel
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600, userSelect: "none" }}>
                    Provider
                  </Typography>
                }
                control={
                  <Checkbox
                    size="small"
                    checked={providerChecked}
                    onChange={handleProviderPreset}
                    sx={{
                      p: 0.5,
                      color: COLORS.GOLD,
                      "&.Mui-checked": { color: COLORS.GOLD },
                    }}
                  />
                }
                sx={{ m: 0 }}
              />
              <FormControlLabel
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600, userSelect: "none" }}>
                    All fields
                  </Typography>
                }
                control={
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    onChange={handleAllPreset}
                    sx={{
                      p: 0.5,
                      color: COLORS.GOLD,
                      "&.Mui-checked": { color: COLORS.GOLD },
                    }}
                  />
                }
                sx={{ m: 0 }}
              />
            </Box>

            <Autocomplete
              multiple
              disableCloseOnSelect
              options={ALL_EXPORT_FIELDS}
              value={selectedFields}
              onChange={(_, newValue) => setSelectedFields(newValue)}
              getOptionLabel={formatFieldLabel}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    checked={selected}
                    sx={{ mr: 1, p: 0.5, color: COLORS.GOLD, "&.Mui-checked": { color: COLORS.GOLD } }}
                  />
                  <Typography variant="body2">{formatFieldLabel(option)}</Typography>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={formatFieldLabel(option)}
                    size="small"
                    sx={{
                      borderRadius: "6px",
                      fontSize: "0.7rem",
                      height: 22,
                      bgcolor: COLORS.goldChipBackground,
                      color: COLORS.GOLD,
                      border: `1px solid ${COLORS.goldChipBorder}`,
                      "& .MuiChip-deleteIcon": { color: COLORS.goldChipDeleteIcon, "&:hover": { color: COLORS.GOLD } },
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedFields.length === 0 ? "Search and select fields..." : ""}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      fontSize: "0.85rem",
                      minHeight: 42,
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.GOLD },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.GOLD },
                    },
                  }}
                />
              )}
              slotProps={{ listbox: { style: { maxHeight: 260 } } }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              mt: 3,
            }}
          >
            <CustomLabel
              text="Export Sessions List"
              variant="h6"
              fontWeight={500}
              sx={{ margin: 0 }}
            />
            <CustomButton
              label={exportButtonLabel}
              onClick={handleStartExport}
              disabled={
                isStarting ||
                exceedsLimit ||
                selectedFields.length === 0 ||
                !hasJwtPermission("GET_ALL_CDRS_CSV")
              }
              bgColor={COLORS.GOLD}
              textColor={COLORS.iconColor}
              size="medium"
              variant="contained"
              borderRadius="12px"
              startIcon={<Export style={{ width: 18, height: 18 }} />}
            />
          </Box>

          <CustomTable<CdrExport> fullBleedMobile={false}
            filters={tableParams}
            fields={fields}
            fetchFunc={useCdrExportsQuery}
            actions={actions}
            sortableFields={[
              "id",
              "createdDate",
              "createdBy",
              "status",
              "count",
            ]}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CdrExportDialog;
